import {useEffect, useReducer} from 'react';
import {reelSeconds} from './reels';
import {studioText} from './studioText';
import type {Brief, Job, JobView, Reel, StudioEstimates} from './types';

/*
 * How long Studio still needs, worked out from the server's timestamps: when the current stage
 * began, when the render began, and when each microreel's preview and file were ready. Before
 * anything is measured the estimate uses the priors below; once a microreel is ready, the time
 * it took stands in for the ones still to come. Older servers send none of these timestamps,
 * so they show no estimate.
 *
 * Drafting is timed from how fast the planner writes: the job's own last planner call, else the
 * server's average over recent calls (GET /options `estimates`), else the priors. The render
 * priors are guesses until tuned from dev runs (the render's LastRender stats). The estimate
 * always says "about" and never counts below zero.
 */

/** Finding the topics in a document or a video's transcript: one short planner call. */
export const TOPICS_S = 20;
/** A planner call's fixed cost before it writes: the prompt and the model's thinking. */
export const PLAN_OVERHEAD_S = 10;
/** Storyboard tokens the planner writes per microreel, by length band. */
export const PLAN_TOKENS_PER_REEL: Record<string, number> = {
  '30-60s': 1800,
  '1-2min': 3200,
  '2-5min': 6000,
};
/** How long each length band's microreels aim to play, as the create page sends it. */
export const BAND_SECONDS: Record<string, number> = {
  '30-60s': 50,
  '1-2min': 90,
  '2-5min': 180,
};
/** How fast the planner model writes through OpenRouter, in output tokens per second. */
export const PLANNER_TOKENS_PER_SECOND = 50;
/** Checking every scene against the source. The microreels are checked in parallel. */
export const CHECK_S = 30;
// The generating priors are double the first guesses, so the estimate errs on the long side.
/** One wave of scene images. */
export const IMAGE_S = 40;
/** How many images the server makes at once (its AssetConcurrency). */
export const ASSET_CONCURRENCY = 8;
/** A microreel's narration, which is all it waits for when its images already exist. */
export const VOICE_S = 16;
/** Preparing a microreel's playable preview once its narration and images exist. */
export const PREPARE_S = 12;
/**
 * Exporting a microreel's MP4, per second of video: double the 4 s staging's render host took.
 * Once a file is exported, its measured time stands in for the rest instead.
 */
export const EXPORT_S_PER_VIDEO_S = 8;
/** A microreel's length when its scenes do not say. */
export const REEL_S = 45;
/** Joining the microreels and saving the files, doubled with the generating priors. */
export const FINISH_S = 30;

export const ALMOST_DONE = studioText.almostDone;
export const TAKING_LONGER = studioText.takingLonger;

export interface Estimate {
  /** Seconds left, always above zero; null once the estimate is used up. */
  seconds: number | null;
  /**
   * "About 2 minutes left", or what to say once the estimate is used up. With several
   * microreels it also says how long the one being made now takes: "About 40 seconds for this
   * one · about 6 minutes for all 3".
   */
  label: string;
  /**
   * While the microreels are made one at a time: seconds until each one that cannot play yet
   * can, by reel id. Zero means it is past its estimate.
   */
  readyIn?: Record<string, number>;
  /**
   * With several microreels: the one being generated or exported now, and the seconds until
   * it is done. Zero means it is past its estimate.
   */
  current?: {reelId: string; seconds: number};
  /** While drafting: how much of the estimate has run, from 0 to 1. */
  progress?: number;
}

/** "40 seconds" or "3 minutes": up to the next 10 seconds below a minute, else whole minutes. */
export function roundedTime(seconds: number): string {
  const tens = Math.max(10, Math.ceil(seconds / 10) * 10);
  if (tens < 60) return studioText.seconds(tens);
  return studioText.minutes(Math.max(1, Math.round(seconds / 60)));
}

/** "about 40 seconds" or "about 3 minutes". */
export const aboutTime = (seconds: number) =>
  studioText.aboutTime(roundedTime(seconds));

/** "Ready in about 40 seconds" for a microreel that cannot play yet. */
export const readyInLabel = (seconds: number) =>
  seconds > 0 ? studioText.readyIn(aboutTime(seconds)) : studioText.almostReady;

/** "About 40 seconds for this one" for the microreel being made now. */
export const thisOneLabel = (seconds: number) =>
  seconds > 0
    ? studioText.thisOne(roundedTime(seconds))
    : studioText.thisOneAlmostDone;

/** "About 6 minutes for all 3 microreels", or "for both microreels" for two. */
export const allReelsLabel = (seconds: number, count: number) =>
  studioText.allReels(roundedTime(seconds), count);

/**
 * A server timestamp in milliseconds. The server writes UTC; a stamp without a zone is read
 * as UTC too, and fractions finer than milliseconds are dropped so every browser parses it.
 */
export function stamp(value: unknown): number | undefined {
  if (typeof value !== 'string') return undefined;
  const parts =
    /^(\d{4}-\d\d-\d\dT\d\d:\d\d(?::\d\d)?)(\.\d+)?(Z|[+-]\d\d:?\d\d)?$/i.exec(
      value.trim(),
    );
  if (!parts) return undefined;
  const ms = parts[2] ? `.${`${parts[2].slice(1)}00`.slice(0, 3)}` : '';
  const zone = (parts[3] || 'Z').replace(/^([+-]\d\d)(\d\d)$/, '$1:$2');
  const time = Date.parse(`${parts[1]}${ms}${zone}`);
  return Number.isFinite(time) ? time : undefined;
}

const FINISHING_STAGE = /^(?:Joining|Finishing)\b/i;
const TOPICS_STAGE = /^Finding the topics/i;

const sum = (values: number[]) => values.reduce((a, b) => a + b, 0);
const latest = (times: Array<number | undefined>) => {
  const known = times.filter((t): t is number => t !== undefined);
  return known.length ? Math.max(...known) : undefined;
};

/** Microreels the planner writes in one go; it plans one per chosen topic. */
const plannedReels = (brief: Brief) => Math.max(1, brief.sections?.length || 0);

const positive = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/** How long each microreel aims to play: the brief's target, else its length band's. */
const reelTarget = (brief: Brief) =>
  positive(brief.targetSeconds)
    ? brief.targetSeconds
    : BAND_SECONDS[brief.lengthBand || ''] ?? BAND_SECONDS['30-60s'];

/** How fast the planner writes: tokens per second of microreel, and tokens per second. */
export interface PlanRates {
  tokensPerVideoSecond: number;
  tokensPerSecond: number;
}

/**
 * The measured planner rates for the job: its own last planner call (a repair pass rewrites the
 * whole storyboard, so it goes the same way), else the server's recent average. Undefined when
 * neither is measured, including a server still on its priors (`sampleSize` 0): the priors here
 * are per length band.
 */
export function planRates(
  job: Job,
  estimates?: StudioEstimates | null,
): PlanRates | undefined {
  const own = job.planStats;
  if (
    own &&
    positive(own.completionTokens) &&
    positive(own.seconds) &&
    positive(own.videoSeconds)
  )
    return {
      tokensPerVideoSecond: own.completionTokens / own.videoSeconds,
      tokensPerSecond: own.completionTokens / own.seconds,
    };
  if (
    estimates &&
    estimates.sampleSize > 0 &&
    positive(estimates.planTokensPerVideoSecond) &&
    positive(estimates.plannerTokensPerSecond)
  )
    return {
      tokensPerVideoSecond: estimates.planTokensPerVideoSecond,
      tokensPerSecond: estimates.plannerTokensPerSecond,
    };
  return undefined;
}

/** One planner pass: a repair pass rewrites the whole storyboard, so it costs the same. */
export const planSeconds = (brief: Brief, rates?: PlanRates) =>
  PLAN_OVERHEAD_S +
  (rates
    ? (plannedReels(brief) * reelTarget(brief) * rates.tokensPerVideoSecond) /
      rates.tokensPerSecond
    : (plannedReels(brief) *
        (PLAN_TOKENS_PER_REEL[brief.lengthBand || ''] ??
          PLAN_TOKENS_PER_REEL['30-60s'])) /
      PLANNER_TOKENS_PER_SECOND);

/** The planner is finding the topics, which the author chooses from before it plans. */
export const findingTopics = (job: Job) =>
  job.status === 'planning' && TOPICS_STAGE.test(job.stage || '');

/**
 * About how long drafting takes once the source is read, and whether that ends with topics to
 * choose rather than the storyboard. While the source is read, which cannot be estimated, it
 * stands in for the countdown; later it is the whole draft's estimate, which does not run down.
 */
export function afterReading(
  job: Job,
  estimates?: StudioEstimates | null,
): {seconds: number; topics: boolean} {
  const {brief} = job;
  if (brief.reelMode === 'per_topic' && !brief.sections?.length)
    return {seconds: TOPICS_S, topics: true};
  return {
    seconds:
      planSeconds(brief, planRates(job, estimates)) +
      (brief.sourceType === 'document' ? CHECK_S : 0),
    topics: false,
  };
}

/** A microreel's preview before any has been measured: its new images, then the preview. */
export function previewSeconds(reel: Reel): number {
  const images = reel.scenes.filter(
    scene =>
      scene.visualKind === 'generated' &&
      !!scene.imagePrompt?.trim() &&
      !scene.imageAsset,
  ).length;
  return (
    Math.max(Math.ceil(images / ASSET_CONCURRENCY) * IMAGE_S, VOICE_S) +
    PREPARE_S
  );
}

export const exportSeconds = (reel: Reel, brief: Brief) =>
  (reelSeconds(reel) || brief.targetSeconds || REEL_S) * EXPORT_S_PER_VIDEO_S;

interface Remaining {
  left: number;
  /** The estimate is used up: what to say instead of a time. */
  overrun?: string;
  readyIn?: Record<string, number>;
  current?: {reelId: string; seconds: number};
  progress?: number;
}

/**
 * Drafting: the current stage's estimate minus the time it has run, plus the check after it.
 * The progress counts the planner's estimate as done once the check is under way.
 */
function planningRemaining(
  job: Job,
  now: number,
  estimates?: StudioEstimates | null,
): Remaining | null {
  const started = stamp(job.stageStartedAt);
  if (started === undefined) return null;
  let stage: number;
  let before = 0;
  let after = 0;
  if (job.status === 'checking') {
    stage = CHECK_S;
    before = planSeconds(job.brief, planRates(job, estimates));
  } else if (findingTopics(job)) stage = TOPICS_S;
  else if (job.status === 'planning') {
    stage = planSeconds(job.brief, planRates(job, estimates));
    if (job.brief.sourceType === 'document') after = CHECK_S;
  }
  // Reading a document or transcribing a video depends on its length, which is not known yet.
  else return null;
  const spent = Math.max(0, now - started) / 1000;
  // A late stage holds at its share until the next one starts.
  const progress = (before + Math.min(spent, stage)) / (before + stage + after);
  // Nothing within a planner call can be measured, so a late one only says it is late.
  return spent < stage
    ? {left: stage - spent + after, progress}
    : {left: 0, overrun: TAKING_LONGER, progress};
}

/**
 * When the render under way began. Queued renders have no start yet; the request is when the
 * stage began.
 */
const renderStart = (job: Job) =>
  stamp(job.renderStartedAt) ?? stamp(job.stageStartedAt);

/** The stamp was made before `start`, by an earlier render. */
const stampedBefore = (value: unknown, start: number) => {
  const time = stamp(value);
  return time !== undefined && time < start;
};

/**
 * The microreels a render under way keeps from an earlier one, which play from its start. Newer
 * servers keep the ones a render that stopped part way made, and any unchanged since: their
 * stamps are from that render. Older servers clear every stamp when a render is requested.
 */
export function keptReels(job: Job): Reel[] {
  const start = renderStart(job);
  return start === undefined
    ? []
    : (job.storyboard?.reels || []).filter(reel =>
        stampedBefore(reel.previewReadyAt, start),
      );
}

/**
 * Generating: each microreel's preview in turn, then each MP4, then the finished files. The
 * step under way counts down to zero and waits there; the steps after it keep their estimate.
 */
function renderRemaining(job: Job, now: number): Remaining | null {
  const reels = job.storyboard?.reels || [];
  const start = renderStart(job);
  if (start === undefined || !reels.length) return null;
  const since = (time: number) => Math.max(0, now - time) / 1000;
  // Stamps from an earlier render are of microreels this one keeps: they are ready, and
  // exported when they were, but took none of this render's time.
  const current = (value: unknown) => {
    const time = stamp(value);
    return time !== undefined && time >= start ? time : undefined;
  };
  const keptReady = reels.map(reel =>
    stampedBefore(reel.previewReadyAt, start),
  );
  const keptExport = reels.map(reel => stampedBefore(reel.renderedAt, start));
  const readyAt = reels.map(reel => current(reel.previewReadyAt));
  const exportedAt = reels.map(reel => current(reel.renderedAt));
  const lastReady = latest(readyAt);
  const lastExport = latest(exportedAt);
  const allExported = exportedAt.every(
    (time, i) => time !== undefined || keptExport[i],
  );

  if (allExported || FINISHING_STAGE.test(job.stage?.trim() || '')) {
    const left =
      FINISH_S - since(lastExport ?? stamp(job.stageStartedAt) ?? start);
    return left > 0 ? {left} : {left: 0, overrun: ALMOST_DONE};
  }

  // With several microreels the one under way gets its own estimate; with one, it is the whole.
  const several = reels.length > 1;
  const waiting = reels.filter(
    (_, i) => readyAt[i] === undefined && !keptReady[i],
  );
  if (waiting.length) {
    // Previews: the ones made so far say how long the rest take.
    const made = readyAt.filter(time => time !== undefined).length;
    const each =
      made && lastReady !== undefined
        ? (lastReady - start) / 1000 / made
        : undefined;
    const steps = waiting.map(reel => each ?? previewSeconds(reel));
    let left = Math.max(0, steps[0] - since(lastReady ?? start));
    const readyIn: Record<string, number> = {};
    waiting.forEach((reel, i) => {
      if (i) left += steps[i];
      readyIn[reel.id] = left;
    });
    const exports = sum(
      reels
        .filter((_, i) => !keptExport[i])
        .map(reel => exportSeconds(reel, job.brief)),
    );
    const making = waiting[0].id;
    return {
      left: left + exports + FINISH_S,
      readyIn,
      ...(several ? {current: {reelId: making, seconds: readyIn[making]}} : {}),
    };
  }

  // MP4s, after every preview: the ones exported so far say how long the rest take.
  const pending = reels.filter(
    (_, i) => exportedAt[i] === undefined && !keptExport[i],
  );
  const exported = exportedAt.filter(time => time !== undefined).length;
  const began = lastReady ?? start;
  const each =
    exported && lastExport !== undefined && lastExport > began
      ? (lastExport - began) / 1000 / exported
      : undefined;
  const steps = pending.map(reel => each ?? exportSeconds(reel, job.brief));
  const thisFile = Math.max(0, steps[0] - since(lastExport ?? began));
  // The last file running past its estimate is nearly done; a fixed "about 20 seconds" is not.
  if (thisFile <= 0 && pending.length === 1)
    return {left: 0, overrun: ALMOST_DONE};
  return {
    left: thisFile + sum(steps.slice(1)) + FINISH_S,
    ...(several ? {current: {reelId: pending[0].id, seconds: thisFile}} : {}),
  };
}

/**
 * About how long until Studio finishes drafting the storyboard or generating the microreels,
 * or null when there is nothing to estimate: the job is not working, is canceling, is reading
 * its source, or the server does not send the timestamps. `estimates` are the server's
 * measured planner rates from GET /options, when it sends them.
 */
export function estimateRemaining(
  view: JobView,
  nowMs: number,
  estimates?: StudioEstimates | null,
): Estimate | null {
  const {job} = view;
  // Every newer server stamps the stage; older ones cannot be estimated.
  if (job.cancellationRequested || stamp(job.stageStartedAt) === undefined)
    return null;
  const remaining = ['rendering', 'approved'].includes(job.status)
    ? renderRemaining(job, nowMs)
    : planningRemaining(job, nowMs, estimates);
  if (!remaining) return null;
  const {left, overrun, readyIn, current, progress} = remaining;
  const count = job.storyboard?.reels.length || 0;
  const estimate: Estimate =
    !overrun && left > 0
      ? {
          seconds: left,
          label: current
            ? studioText.thisOneAndAll(
                thisOneLabel(current.seconds),
                aboutTime(left),
                count,
              )
            : studioText.timeLeft(roundedTime(left)),
        }
      : {seconds: null, label: overrun || ALMOST_DONE};
  return {
    ...estimate,
    ...(readyIn ? {readyIn} : {}),
    ...(current && estimate.seconds !== null ? {current} : {}),
    ...(progress !== undefined ? {progress} : {}),
  };
}

/**
 * The current time, rendering again every `intervalMs` while `active`, so a countdown stays
 * fresh between the page's polls.
 */
export function useNow(active: boolean, intervalMs = 1000): number {
  const [, tick] = useReducer((count: number) => count + 1, 0);
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(tick, intervalMs);
    return () => clearInterval(timer);
  }, [active, intervalMs]);
  return Date.now();
}
